<template lang="pug">
#bulk-send.tab-pane.fade.show.active(
  aria-labelledby='bulk-send-tab'
  role='tabpanel'
)
  .row(v-if='!showFeedbackNoBulkSends')
    .mb-3.col-12.col-lg-6.col-xl-5.d-flex.align-items-center.gap-2
      .form-control-icon--end-line.flex-grow-1
        input.form-control.rounded-3(
          :placeholder='$t("pages.bulkSend.searchPlaceholder")'
          @input='searchBulkSends()'
          autocomplete='off'
          v-model='filters.search'
        )
        icon.icon--blue.font-size-subtitle(name='magnifying-glass')
      filter-selector.me-auto(
        :filters='mountFilterConfig'
        @update='updateFilter'
      )
    .mb-3.col-12.col-lg-6.col-xl-7
      .text-end
        button.btn.btn-primary(@click='createBulkSend') {{ $t('newBulkSend') }}
  .row(v-if='!isEmptyList')
    .col-12
      bulk-sends-card-table.mb-5(
        :data='bulkSends.data'
        :sort='filters.sort'
        @add-sort='addSort'
      )
      simple-paginator(:payload='bulkSends' @navigate='fetchBulkSends')
  .text-center.mt-5(v-if='showFeedbackNoBulkSends')
    img.img(
      src='/img/ilustrations/no-document-template.svg'
      style='max-height: 260px'
    )
    h5.mt-4 {{ $t('pages.bulkSend.emptyTitle') }}
    p.font-color-blue.mt-2
      | {{ $t('pages.bulkSend.emptyDescription') }}
    button.btn.btn-primary.mt-3(@click='createBulkSend') {{ $t('newBulkSend') }}

  .text-center.mt-5(v-if='showFeedbackNoResults')
    img.img-fluid(src='/img/ilustrations/no-contacts-found.svg')
    p.font-color-blue.text-center.mt-4
      | {{ $t('message.no_results_found') }}
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
import EnvelopesService from '@/modules/business/services/http/envelopes/EnvelopesService';
import debounce from 'debounce';
import SimplePaginator from '@/ui/organisms/SimplePaginator.vue';
import alert from '@/modules/account/common/alert';
import BulkSendsCardTable from '@/modules/business/components/organisms/card-table/BulkSendsCardTable.vue';
import FilterSelector from '@/ui/organisms/FilterSelector.vue';
import BulkSendStatus from '@/enums/BulkSendStatus';

export default {
  name: 'DocumentsBulkSendPage',
  components: {
    FilterSelector,
    Icon,
    SimplePaginator,
    BulkSendsCardTable,
  },
  data() {
    return {
      bulkSends: {
        data: [],
        links: {},
        meta: {},
      },
      filters: {
        search: '',
        status: '',
        sort: '-created_at',
      },
    };
  },
  created() {
    this.$store.dispatch('page/setTitle', this.$t('pages.bulkSend.tabTitle'));
    this.$store.dispatch('page/setHead', {
      title: this.$t('pages.draft.title'),
    });
    this.$store.dispatch('page/setClass', 'business documents');
    if (
      this.$route.params.status &&
      BulkSendStatus.getAllStatusTranslated(this.$i18n).find(
        (item) => item.value === this.$route.params.status
      )
    ) {
      this.filters.status = this.$route.params.status;
    }
    this.fetchBulkSends();
  },
  computed: {
    isEmptyList() {
      return this.bulkSends.data.length === 0;
    },
    isSearching() {
      return this.filters.search.length > 0;
    },
    showFeedbackNoBulkSends() {
      return this.isEmptyList && !this.isSearching;
    },
    showFeedbackNoResults() {
      return this.isEmptyList && this.isSearching;
    },
    getAllStatus() {
      return BulkSendStatus.getAllStatusTranslated(this.$i18n);
    },
    mountFilterConfig() {
      return [
        {
          name: 'status',
          label: this.$t('bulkSendsCardTable.bulkSendStatus'),
          selected: this.$route.params.status,
          options: [
            {
              label: this.$t('sentEnvelopesCardTable.filter.all'),
              value: '',
            },
          ].concat(this.getAllStatus),
        },
      ];
    },
  },
  methods: {
    addSort(column, type) {
      this.filters.sort = `${type === 'DESC' ? '-' : ''}${column}`;
      this.fetchBulkSends();
    },
    fetchBulkSends(page = 1) {
      this.$loading(true);
      this.bulkSends.data = [];
      const params = {
        ...(this.filters.status && { 'filter[status]': this.filters.status }),
        ...(this.filters.search && { q: this.filters.search }),
        ...{ sort: this.filters.sort },
        page,
      };
      EnvelopesService.listBulkSends(params)
        .then((response) => {
          this.bulkSends = response.data;
        })
        .catch((error) => {
          alert.fireAlert(
            `${this.$t('pages.bulkSend.listError')} ${
              error.response.data.message
            }`,
            {
              classes: 'alert-danger',
              styles:
                'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
              tag: 'httpAlert',
              icon: 'triangle-exclamation',
            }
          );
        })
        .finally(() => {
          this.$loading(false);
        });
    },
    updateFilter(selectedFilters) {
      Object.keys(selectedFilters).forEach((key) => {
        this.filters[key] = selectedFilters[key].value;
      });
      this.fetchBulkSends();
    },
    createBulkSend() {
      // go to bulk send creation page - step 1
      this.$router.push({
        name: 'business.bulk-send.new',
        params: {
          isBulkSend: true,
        },
      });
    },
    searchBulkSends: debounce(function searchBulkSends() {
      this.fetchBulkSends();
    }, 300),
  },
};
</script>

<style lang="stylus" scoped>
@media screen and (min-width: 768px)
  .form-control-icon--end-line
    max-width: 260px
</style>
